/**
 * 알림 화면 공용 메타 — 유형 매핑·아이콘·상대 시각·경로.
 *
 * NotificationCenterPage(인박스) 와 NotificationDetailPage(상세) 가 공유한다.
 *
 * ── 2026-08-25 peeka 재정의 ─────────────────────────────────────────
 * 전에는 템플릿(픽쿠리) 도메인이 그대로 있었다 — 탭 라벨만 peeka 로 바꾸고
 * 아래 매핑은 KUJI_OPEN·INV_EXPRY·SHIP_CHG·GIFT_RCV 였다. 즉 '관심 카드' 탭이
 * 쿠지 알림을 거르는 구조였고, 배지에 '포인트 소멸'·'선물 반환'·'내 차례' 가
 * 남아 있었다.
 *
 * 화면정의서 rev10 · 기능명세 FN-063~067 이 유형을 **4종으로 확정**해 그대로 옮겼다:
 *   ① 시세 변동 (FN-064)   ② 컬렉션 총액 요약 (FN-065)
 *   ③ 발견/트렌드 (FN-066)  ④ 시스템/공지 (FN-067)
 *
 * ⚠ 문구는 여기 두지 않는다. 전에는 한국어가 이 파일에 박혀 있어 i18n 밖이었다 —
 *   지금은 키만 돌려주고 번역은 화면이 한다.
 * ⚠ BE 의 NOTI_DIV 코드는 아직 peeka 기준으로 확정되지 않았다. 아래 코드 이름은
 *   **우리가 먼저 적어 둔 제안**이고, BE 가 정해지면 CODE_TO_TYPE 만 갈면 된다.
 *   모르는 코드는 system 으로 떨어져 리스트에서 누락되지 않는다.
 */
import { env } from "../../config/env";


import type { IconName } from "../../components/primitives/Icon";

/** 화면정의서 SC-022 유형 필터 · SC-024 유형별 on/off 와 같은 4종 */
export type NotificationTypeKey = "price" | "collection" | "discover" | "system";

export const NOTIFICATION_TYPES: readonly NotificationTypeKey[] = [
  "price",
  "collection",
  "discover",
  "system",
];

/**
 * BE raw NOTI_DIV 코드 → peeka 유형.
 *
 * 미등록 코드는 **system(시스템·공지)** 폴백 — 공지가 catch-all 이라 새 코드가
 * 생겨도 인박스에서 사라지지 않는다.
 */
const CODE_TO_TYPE: Record<string, NotificationTypeKey> = {
  // ① 시세 변동 — 단건 · 동시 다발 시 묶음(digest) 1건 (FN-064)
  PRICE_ALERT: "price",
  PRICE_DIGEST: "price",
  // ② 컬렉션 총액 요약 — 일/주간 (FN-065)
  COLLECTION_SUMMARY: "collection",
  // ③ 발견/트렌드 — 인기 기준 (FN-066)
  TREND_RISING: "discover",
  TREND_NEW: "discover",
  // ④ 시스템/공지 (FN-067)
  NOTICE: "system",
  MAINTENANCE: "system",
  APP_UPDATE: "system",
  REVIEW_REQUEST: "system",
  NEW_DEVICE_LOGIN: "system",
};

/** 유형별 아이콘 (Icon 컴포넌트 이름).
 *
 * ⚠ 발견에만 번개를 쓴다. BRAND §3.1 규칙 ② — **전기 은유는 인기에만**,
 *   가격에는 쓰지 않는다. 그래서 시세는 추세 화살표다. */
const TYPE_ICON: Record<NotificationTypeKey, IconName> = {
  price: "GraphUp",
  collection: "Storage",
  discover: "Lightning",
  system: "Info",
};

export function typeForCode(code: string): NotificationTypeKey {
  return CODE_TO_TYPE[code] ?? "system";
}

export function iconForCode(code: string): IconName {
  return TYPE_ICON[typeForCode(code)];
}

/** 유형 라벨 i18n 키 — 문구는 화면이 번역한다 */
export function typeLabelKey(type: NotificationTypeKey): string {
  return `notification:type.${type}`;
}

export function matchesType(
  code: string,
  filter: NotificationTypeKey | "all",
): boolean {
  return filter === "all" || typeForCode(code) === filter;
}

/**
 * 상세 페이지로 보낼 유형인지 — 본문 위주라 갈 대상이 따로 없는 알림.
 * 시세·컬렉션·발견은 카드/컬렉션/트렌드로 가므로 상세가 없다.
 */
export function hasDetailView(code: string): boolean {
  return typeForCode(code) === "system";
}

/**
 * 행이 죽었는가 — 화면정의서 '대상(카드/컬렉션) 삭제·비공개: 해당 행 비활성'.
 *
 * 갈 곳이 있어야 하는 유형(시세·컬렉션·발견)인데 딥링크가 없으면 대상이
 * 사라진 것으로 본다. 공지는 애초에 대상이 없으니 해당하지 않는다.
 */
export function isTargetGone(code: string, deepLink?: string): boolean {
  return !deepLink && !hasDetailView(code);
}

/**
 * 상대 시각 (화면정의서 SC-022 '상대 시각').
 *
 * 30일이 넘으면 절대 날짜로 떨어진다 — 인박스가 30일까지만 보여주므로 그 밖은
 * 예외적으로 남은 것이고, '두 달 전' 보다 날짜가 정확하다.
 *
 * ⚠ 문구는 i18n 이 만든다. 여기서는 **어떤 키를 몇으로 쓸지**만 정한다.
 */
export function relativeTimeKey(
  iso: string,
  now: number = Date.now(),
): { key: string; count?: number; date?: string } {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return { key: "time.unknown" };
  const sec = Math.max(0, Math.floor((now - t) / 1000));
  if (sec < 60) return { key: "time.justNow" };
  const min = Math.floor(sec / 60);
  if (min < 60) return { key: "time.minutes", count: min };
  const hour = Math.floor(min / 60);
  if (hour < 24) return { key: "time.hours", count: hour };
  const day = Math.floor(hour / 24);
  if (day === 1) return { key: "time.yesterday" };
  if (day < 30) return { key: "time.days", count: day };
  return { key: "time.absolute", date: formatNotificationDate(iso) };
}

/** ISO datetime → 'YYYY.MM.DD' — 30일 넘은 알림에만 쓴다. */
export function formatNotificationDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}.${m}.${day}`;
}

/**
 * deepLink(예: peeka://shipping/status/1) → 앱 내부 경로(/shipping/status/1).
 * 스킴은 env(VITE_APP_DEEP_LINK_SCHEME)에서 읽는다 — AndroidManifest·Info.plist 와 같은 값이어야 한다.
 *
 * ⚠ 옛 스킴(template://)도 받아 준다 (design 2026-08-25) — BE·푸시 페이로드가
 *   아직 그걸 보낼 수 있고, 알림을 눌렀는데 아무 데도 못 가는 것이 제일 나쁘다.
 */
export function resolveDeepLinkPath(deepLink: string): string {
  const prefixes = [`${env.VITE_APP_DEEP_LINK_SCHEME}://`, "template://"];
  const prefix = prefixes.find((p) => deepLink.startsWith(p));
  return prefix ? "/" + deepLink.slice(prefix.length) : deepLink;
}

/**
 * 알림을 눌렀을 때 갈 경로. 기본은 deepLink 그대로.
 *
 * 유형별 예외가 생기면(예: 지난 트렌드는 상세로 강등) 여기에 분기를 둔다.
 */
export function resolveNotificationPath(_code: string, deepLink: string): string {
  return resolveDeepLinkPath(deepLink);
}
